import { Video, Share2, Target, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import socialMediaImage from '@/assets/shooting-action.jpg';
import videographyImage from '@/assets/shooting-outdoor-event.jpg';
import contentStrategyImage from '@/assets/content-strategy.jpg';

const Services = () => {
  const services = [
    {
      icon: Share2,
      title: 'Social Media Management',
      description:
        'We handle your entire social presence—from strategy and content creation to engagement and growth. Focus on your business while we build your brand.',
      features: [
        'Custom strategy & research',
        'Content calendar & scheduling',
        'Community engagement',
        'Analytics & reporting',
        'Ad campaign management',
      ],
      image: socialMediaImage,
      color: 'bg-accent',
      link: '/services/social-media',
    },
    {
      icon: Video,
      title: 'Video Production',
      description:
        'Cinematic, professional videos that tell your story and captivate your audience. From brand films to promotional content—we bring your vision to life.',
      features: [
        'Full-service production',
        'Professional editing',
        'Short & long-form content',
        '4K quality equipment',
        'Enhanced audio',
      ],
      image: videographyImage,
      color: 'bg-secondary',
      link: '/services/video-production',
    },
    {
      icon: Target,
      title: 'Content Strategy',
      description:
        'Data-driven content strategies designed to increase engagement, generate leads, and grow your brand online. Every piece of content has a purpose.',
      features: [
        'Audience research',
        'Content planning',
        'Performance tracking',
        'Competitive analysis',
        'Brand voice development',
      ],
      image: contentStrategyImage,
      color: 'bg-primary',
      link: '/services/content-strategy',
    },
  ];

  return (
    <section id="services" className="py-16 sm:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-3 sm:mb-4">
            Our Services
          </span>
          <h2 className="font-display font-bold text-2xl sm:text-4xl lg:text-5xl text-foreground mb-4 sm:mb-6">
            Everything You Need to
            <span className="text-accent"> Dominate Online</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground">
            Comprehensive media solutions tailored for professionals who want to stand out, build trust, and attract premium clients.
          </p>
        </div>

        {/* Services Grid */}
        <div className="space-y-12 sm:space-y-20">
          {services.map((service, index) => (
            <div
              key={service.title}
              className={`flex flex-col ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'
              } gap-6 sm:gap-12 items-center`}
            >
              {/* Image */}
              <div className="w-full lg:w-1/2">
                <div className="relative group">
                  <div className={`absolute -inset-4 ${service.color}/10 rounded-2xl blur-2xl group-hover:blur-3xl transition-all duration-500`} />
                  <img
                    src={service.image}
                    alt={service.title}
                    className="relative w-full aspect-[4/3] sm:aspect-square object-cover rounded-2xl shadow-strong"
                  />
                  <div className={`absolute top-4 left-4 sm:top-6 sm:left-6 ${service.color} p-3 sm:p-4 rounded-xl shadow-medium`}>
                    <service.icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary-foreground" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="w-full lg:w-1/2">
                <h3 className="font-display font-bold text-xl sm:text-3xl text-foreground mb-3 sm:mb-4">
                  {service.title}
                </h3>
                <p className="text-base sm:text-lg text-muted-foreground mb-4 sm:mb-6">
                  {service.description}
                </p>
                <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-accent" />
                      </div>
                      <span className="text-sm sm:text-base text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="default" size="lg" className="group w-full sm:w-auto" asChild>
                  <Link to={service.link}>
                    Learn More About {service.title}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
